<template>
    <BaseCard padding="md" class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div class="flex-1 min-w-0 flex flex-col gap-1">
            <div class="body-semibold text-ink truncate">{{ cycle.template.name }}</div>
            <div class="scaffold-body-small text-ink-muted">
                <template v-if="cycle.awaiting_self_reflection">
                    {{ t("reflections.active360.pastRecordedAwaitingSelf", { responded: cycle.responded_count, total: Math.max(cycle.total_invited, 1) }) }}
                </template>
                <template v-else>{{ statusLabel }}</template>
            </div>
            <div v-if="dateLabel" class="scaffold-body-small text-ink-muted">{{ dateLabel }}</div>
        </div>
        <a
            v-if="cycle.awaiting_self_reflection"
            :href="`/reflections/360/${cycle.id}/results`"
            class="base-btn-primary base-btn-sm sm:shrink-0 whitespace-nowrap text-center self-start sm:self-auto"
            :aria-label="t('reflections.shared.actionLabel', { action: t('reflections.active360.pastComplete'), target: cycle.template.name })"
            @click.prevent="emit('view', cycle.id)">
            {{ t("reflections.active360.pastComplete") }}
        </a>
        <a
            v-else-if="cycle.has_report"
            :href="`/reflections/360/${cycle.id}/results`"
            class="base-btn-neutral base-btn-sm sm:shrink-0 whitespace-nowrap text-center self-start sm:self-auto"
            :aria-label="t('reflections.shared.actionLabel', { action: t('reflections.sixty.past.viewResults'), target: cycle.template.name })"
            @click.prevent="emit('view', cycle.id)">
            {{ t("reflections.sixty.past.viewResults") }}
        </a>
        <!-- A closed cycle with no report is terminal (raters fell short, or
             the wait for the self reflection timed out), even when it had
             unlocked. Otherwise no report with `unlocked_at` set means the
             report is legitimately on its way. -->
        <span v-else-if="cycle.closed_awaiting_self_reflection" class="details text-ink-muted sm:shrink-0 sm:self-center">{{
            t("reflections.sixty.past.closedAwaitingSelfReflection")
        }}</span>
        <span v-else-if="cycle.closed_without_report" class="details text-ink-muted sm:shrink-0 sm:self-center">{{ t("reflections.sixty.past.closedWithoutReport") }}</span>
        <BaseButton
            v-else-if="cycle.unlocked_at"
            type="button"
            theme="neutral"
            size="sm"
            disabled
            class="shrink-0 self-start sm:self-auto whitespace-nowrap flex items-center gap-2">
            <i class="bi bi-arrow-repeat animate-spin" aria-hidden="true"></i>
            {{ t("reflections.sixty.past.generating") }}
        </BaseButton>
        <span v-else class="details text-ink-muted sm:shrink-0 sm:self-center">
            {{ cycle.never_started ? t("reflections.sixty.past.neverCompleted") : t("reflections.sixty.past.closedWithoutReport") }}
        </span>
    </BaseCard>
</template>

<script setup lang="ts">
import type { PastCycleSummary } from "~vue/api/generated/reflections";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import { formatDate } from "~vue/utils/threeSixty";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    cycle: PastCycleSummary;
    kind: "sixty" | "self";
}

const { cycle, kind } = defineProps<Props>();
const emit = defineEmits<{ (event: "view", cycleId: string): void }>();
const { t } = useI18n();

const dateLabel = computed(() => formatDate(cycle.unlocked_at || cycle.closed_at, "long"));

// A cycle that expired while still a draft was never launched: there are no
// responses to count and nothing was completed, so say that rather than
// "0 of 1 responses" or "Completed reflection". A draft can already hold
// nominated raters, so acknowledge them instead of implying they were lost.
const statusLabel = computed(() => {
    if (cycle.never_started) {
        if (kind !== "sixty") {
            return t("reflections.sixty.past.closedBeforeStarted");
        }
        if (cycle.total_invited === 0) {
            return t("reflections.sixty.past.neverLaunched");
        }
        return t("reflections.sixty.past.neverLaunchedNominated", { count: cycle.total_invited }, cycle.total_invited);
    }
    if (kind === "sixty") {
        return t("reflections.sixty.card.responsesRecorded", { responded: cycle.responded_count, total: Math.max(cycle.total_invited, 1) });
    }
    return cycle.unlocked_at ? t("reflections.sixty.past.completed") : t("reflections.sixty.past.closedBeforeSubmitted");
});
</script>
